import { memo, useCallback, useRef, useState } from 'react';
import { Box, Text, theme, type ThemeSpaces, Tooltip } from '@frameio/vapor';
import { IconInfo } from '@frameio/vapor-icons';
import { HighlightedText } from '../../components/HighlightedText';
import { useCustomShortcuts } from '../context/CustomShortcutsContext';
import { useKeyboardShortcutsTracking } from '../hooks/useKeyboardShortcutsTracking';
import type { Command } from '../types/Command';
import type { CommandKeys } from '../types/CommandKeys';
import { splitKeyboardShortcut } from '../utils/getKeyboardShortcutString';
import styles from './CommandRegistryHelpDialogRow.module.css';
import { Keycap } from './Keycap';
import {
  ShortcutCaptureInput,
  type ShortcutCaptureInputHandle,
} from './ShortcutCaptureInput/ShortcutCaptureInput';

type RowTextStrings = {
  or: string;
};

type Props = {
  command: Command & { keyMatchPositions?: readonly boolean[] };
  /** Original (non-customized) keys for this command. Required for edit mode. */
  defaultKeys?: ReadonlyArray<CommandKeys>;
  searchTerm: string;
  isApple: boolean;
  /** True when this row's ShortcutCaptureInput is currently focused/recording. */
  isRecording?: boolean;
  /** Called when this row enters/leaves recording mode. */
  onRecordingChange?: (id: string, isRecording: boolean) => void;
  /**
   * When true, the row is tagged with the `data-onboarding-anchor` attribute
   * so the onboarding tooltip system can anchor a discovery tooltip to it and
   * highlight the entire row. Only one row in the dialog should carry this
   * flag at a time.
   */
  isOnboardingAnchor?: boolean;
  paddingTop: ThemeSpaces;
  paddingBottom: ThemeSpaces;
  getActionModifierLabel: (modifier: string) => string;
  text: RowTextStrings;
};

function CommandRegistryHelpDialogRowImpl({
  command,
  defaultKeys,
  searchTerm,
  isApple,
  isRecording = false,
  onRecordingChange,
  isOnboardingAnchor = false,
  paddingTop,
  paddingBottom,
  getActionModifierLabel,
  text,
}: Props) {
  const customShortcuts = useCustomShortcuts();
  const { trackCustomized, trackReset } = useKeyboardShortcutsTracking();

  const handleInputRecordingChange = useCallback(
    (recording: boolean) => {
      onRecordingChange?.(command.id, recording);
    },
    [command.id, onRecordingChange]
  );

  const canCustomize = Boolean(
    command.isCustomizable && customShortcuts && defaultKeys
  );

  const handleChange = useCallback(
    ({ id, keys, reset }: { id: string; keys?: string[]; reset?: boolean }) => {
      if (!customShortcuts) return;
      // Read the binding before mutating so the analytics event can report
      // what changed: the customized value if one existed, else the default.
      const previousCustomized = customShortcuts.customizedShortcuts?.[id];
      const wasPreviouslyCustomized = previousCustomized !== undefined;
      const previousKeys =
        previousCustomized ?? (defaultKeys ? [...defaultKeys] : null);

      if (reset) {
        // Only the explicit Reset button (↺) returns the shortcut to its
        // default. Clearing the input (empty keys array) is treated as a
        // user-initiated modification that persists as an empty binding.
        customShortcuts.resetShortcut(id);
        trackReset({ scope: 'Single', commandId: id });
      } else if (keys) {
        customShortcuts.setShortcut(id, keys as CommandKeys[]);
        trackCustomized({
          commandId: id,
          // Clearing the input persists as an empty array; report empty → null.
          newKeys: keys.length > 0 ? keys : null,
          previousKeys,
          wasPreviouslyCustomized,
        });
      }
    },
    [customShortcuts, defaultKeys, trackCustomized, trackReset]
  );

  const customizedShortcuts = customShortcuts?.customizedShortcuts;
  const customizedValue = customizedShortcuts?.[command.id];

  const [isRowHovered, setIsRowHovered] = useState(false);
  const shortcutRef = useRef<ShortcutCaptureInputHandle>(null);
  // Capture recording state at mousedown — before the field's blur flips it —
  // so the click handler can tell "clicking to exit edit mode" apart from
  // "clicking an idle row to start". Without this, the blur exits recording and
  // the trailing click immediately re-enters it (a visible glitch).
  const wasRecordingOnMouseDownRef = useRef(false);

  const handleRowMouseDown = useCallback(() => {
    wasRecordingOnMouseDownRef.current = isRecording;
  }, [isRecording]);

  const handleRowClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (!canCustomize) return;
      if ((e.target as HTMLElement).closest('button, a, input')) return;
      // Clicking the row while recording just exits edit mode (the field blurs);
      // don't re-enter recording on the same click.
      if (wasRecordingOnMouseDownRef.current || isRecording) return;
      shortcutRef.current?.startRecording();
    },
    [canCustomize, isRecording]
  );

  const primaryKeycaps = (
    isApple
      ? splitKeyboardShortcut(command?.displayKeys)
      : splitKeyboardShortcut(command?.displayKeys, '+')
  )?.map((key, i) => (
    <Keycap key={key} $isSearch={command?.keyMatchPositions?.[i]}>
      {key}
    </Keycap>
  ));

  return (
    <div
      className={[
        styles.row,
        canCustomize ? styles.rowInteractive : '',
        canCustomize && isRowHovered && !isRecording
          ? styles.rowInteractiveHovered
          : '',
      ]
        .filter(Boolean)
        .join(' ')}
      data-onboarding-anchor={
        isOnboardingAnchor ? 'custom-shortcuts-capture' : undefined
      }
      onClick={handleRowClick}
      onMouseDown={canCustomize ? handleRowMouseDown : undefined}
      onMouseEnter={canCustomize ? () => setIsRowHovered(true) : undefined}
      onMouseLeave={canCustomize ? () => setIsRowHovered(false) : undefined}
      style={{
        paddingBottom: `var(--spaces-${paddingBottom})`,
        paddingTop: `var(--spaces-${paddingTop})`,
      }}
    >
      {/* `minWidth={0}` lets the label container shrink past its intrinsic
          content size so long labels wrap text internally instead of forcing
          the row to wrap (which would push the input below the label when
          the input grows to its 185px recording width). */}
      <Box display="flex" gap={4} minWidth={0}>
        <HighlightedText
          color="body"
          highlightText={searchTerm}
          style={{ flexGrow: 1 }}
          variant="secondary"
        >
          {command?.displayLabel}
        </HighlightedText>
        {command?.tooltip && (
          <Tooltip
            maxWidth="189px"
            placement="bottom"
            title={command?.tooltip ?? ''}
          >
            <IconInfo color={theme.colors.icon.secondary} size={16} />
          </Tooltip>
        )}
      </Box>

      {/* Right-side controls keep their full width even when recording — the
          label shrinks to make room. */}
      <Box alignItems="center" display="flex" flexShrink="0" gap={4}>
        {canCustomize && defaultKeys ? (
          <ShortcutCaptureInput
            ref={shortcutRef}
            customizedValue={customizedValue}
            defaultValue={[...defaultKeys]}
            id={command.id}
            isRowHovered={isRowHovered}
            onChange={handleChange}
            onRecordingChange={handleInputRecordingChange}
            variant="inline"
          />
        ) : (
          <span className={styles.shortcutKeys}>{primaryKeycaps}</span>
        )}

        {!canCustomize && command?.alternativeDisplayKeys && (
          <Box alignItems="center" display="flex" height={24}>
            <Text color="tertiary" variant="caption">
              {text.or}
            </Text>
          </Box>
        )}

        {!canCustomize &&
          (isApple
            ? [...(command?.alternativeDisplayKeys || [])]
            : (command?.alternativeDisplayKeys || '')
                .split('+')
                .filter(Boolean) || []
          )?.map((key, i) => (
            <Keycap key={key} $isSearch={command?.keyMatchPositions?.[i]}>
              {key}
            </Keycap>
          ))}
        {command?.actionModifiers?.map((key, i) => (
          <Keycap
            key={key}
            $isHovered={isRowHovered && !isRecording}
            $isSearch={command?.keyMatchPositions?.[i]}
          >
            {getActionModifierLabel(key)}
          </Keycap>
        ))}
      </Box>
    </div>
  );
}

// Memoized so dialog-wide re-renders (e.g. `setRecordingIds` flipping for a
// sibling row) skip rows whose props are referentially stable. Relies on the
// dialog memoizing `text`, `getActionModifierLabel`, and the per-row `command`
// ref so this shallow compare can actually bail out.
export const CommandRegistryHelpDialogRow = memo(
  CommandRegistryHelpDialogRowImpl
);
